import { db, storage } from '../firebase';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  getDocs, 
  query, 
  where, 
  orderBy, 
  serverTimestamp,
  getDoc 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Resource Service for managing project resources
 */

/**
 * Upload a new resource for a project
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID who is uploading
 * @param {Object} resourceData - Resource metadata
 * @param {File} file - File to upload (optional for links)
 * @returns {Promise<string>} - Resource ID
 */
export const uploadResource = async (projectId, userId, resourceData, file = null) => {
  try {
    let fileUrl = '';
    let fileName = '';
    let fileType = '';
    let fileSize = 0;

    // Upload file to Firebase Storage if provided
    if (file) {
      const timestamp = Date.now();
      const storageRef = ref(storage, `projects/${projectId}/resources/${timestamp}_${file.name}`);
      
      await uploadBytes(storageRef, file);
      fileUrl = await getDownloadURL(storageRef);
      fileName = file.name;
      fileType = file.type;
      fileSize = file.size;
    } else if (resourceData.url) {
      // For link resources
      fileUrl = resourceData.url;
    }

    // Create resource document
    const resource = {
      projectId,
      title: resourceData.title,
      description: resourceData.description || '',
      resourceType: resourceData.resourceType || 'document',
      url: fileUrl,
      fileUrl: fileUrl, // For compatibility
      fileName,
      fileType,
      fileSize,
      uploadedBy: resourceData.uploadedBy || 'Unknown',
      uploadedById: userId,
      uploadedAt: serverTimestamp(),
      isActive: true
    };

    const resourceRef = await addDoc(collection(db, 'projectResources'), resource);

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId,
      type: 'resource_uploaded',
      description: `New resource uploaded: ${resourceData.title}`,
      resourceId: resourceRef.id,
      userId,
      timestamp: serverTimestamp()
    });

    return resourceRef.id;
  } catch (error) {
    console.error('Error uploading resource:', error);
    throw new Error('Failed to upload resource: ' + error.message);
  }
};

/**
 * Get all resources for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Array>} - Array of resources
 */
export const getProjectResources = async (projectId) => {
  try {
    // Temporarily remove orderBy to avoid composite index requirement for local testing
    const resourcesQuery = query(
      collection(db, 'projectResources'),
      where('projectId', '==', projectId),
      where('isActive', '==', true)
      // orderBy('uploadedAt', 'desc') // Will add back after index is deployed
    );

    const querySnapshot = await getDocs(resourcesQuery);
    const resources = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort manually for now
    return resources.sort((a, b) => {
      if (a.uploadedAt && b.uploadedAt) {
        const aTime = a.uploadedAt.seconds ? a.uploadedAt.seconds * 1000 : new Date(a.uploadedAt).getTime();
        const bTime = b.uploadedAt.seconds ? b.uploadedAt.seconds * 1000 : new Date(b.uploadedAt).getTime();
        return bTime - aTime;
      }
      return 0;
    });
  } catch (error) {
    console.error('Error fetching project resources:', error);
    throw new Error('Failed to fetch resources: ' + error.message);
  }
};

/**
 * Delete a resource
 * @param {string} resourceId - Resource ID
 * @param {string} projectId - Project ID
 * @param {string} userId - User ID who is deleting
 * @returns {Promise<void>}
 */
export const deleteResource = async (resourceId, projectId, userId) => {
  try {
    // Get resource data first
    const resourceDoc = await getDoc(doc(db, 'projectResources', resourceId));
    if (!resourceDoc.exists()) {
      throw new Error('Resource not found');
    }

    const resourceData = resourceDoc.data();

    // Delete file from storage if it exists
    if (resourceData.fileUrl && !resourceData.fileUrl.startsWith('http')) {
      try {
        const fileRef = ref(storage, resourceData.fileUrl);
        await deleteObject(fileRef);
      } catch (storageError) {
        console.warn('Could not delete file from storage:', storageError);
      }
    }

    // Soft delete resource (mark as inactive)
    await updateDoc(doc(db, 'projectResources', resourceId), {
      isActive: false,
      deletedAt: serverTimestamp(),
      deletedBy: userId
    });

    // Create activity log
    await addDoc(collection(db, 'activities'), {
      projectId,
      type: 'resource_deleted',
      description: `Resource deleted: ${resourceData.title}`,
      resourceId,
      userId,
      timestamp: serverTimestamp()
    });

  } catch (error) {
    console.error('Error deleting resource:', error);
    throw new Error('Failed to delete resource: ' + error.message);
  }
};

/**
 * Update resource metadata
 * @param {string} resourceId - Resource ID
 * @param {Object} updates - Updates to apply
 * @param {string} userId - User ID who is updating
 * @returns {Promise<void>}
 */
export const updateResource = async (resourceId, updates, userId) => {
  try {
    await updateDoc(doc(db, 'projectResources', resourceId), {
      ...updates,
      updatedAt: serverTimestamp(),
      updatedBy: userId
    });
  } catch (error) {
    console.error('Error updating resource:', error);
    throw new Error('Failed to update resource: ' + error.message);
  }
};

/**
 * Get resource by ID
 * @param {string} resourceId - Resource ID
 * @returns {Promise<Object>} - Resource data
 */
export const getResourceById = async (resourceId) => {
  try {
    const resourceDoc = await getDoc(doc(db, 'projectResources', resourceId));
    if (!resourceDoc.exists()) {
      throw new Error('Resource not found');
    }
    return { id: resourceDoc.id, ...resourceDoc.data() };
  } catch (error) {
    console.error('Error fetching resource:', error);
    throw new Error('Failed to fetch resource: ' + error.message);
  }
};

/**
 * Get resources by type for a project
 * @param {string} projectId - Project ID
 * @param {string} resourceType - Type of resource (document, video, link)
 * @returns {Promise<Array>} - Array of resources
 */
export const getResourcesByType = async (projectId, resourceType) => {
  try {
    // Temporarily remove orderBy to avoid composite index requirement for local testing
    const resourcesQuery = query(
      collection(db, 'projectResources'),
      where('projectId', '==', projectId),
      where('resourceType', '==', resourceType),
      where('isActive', '==', true)
      // orderBy('uploadedAt', 'desc') // Will add back after index is deployed
    );

    const querySnapshot = await getDocs(resourcesQuery);
    const resources = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Sort manually for now
    return resources.sort((a, b) => {
      if (a.uploadedAt && b.uploadedAt) {
        const aTime = a.uploadedAt.seconds ? a.uploadedAt.seconds * 1000 : new Date(a.uploadedAt).getTime();
        const bTime = b.uploadedAt.seconds ? b.uploadedAt.seconds * 1000 : new Date(b.uploadedAt).getTime();
        return bTime - aTime;
      }
      return 0;
    });
  } catch (error) {
    console.error('Error fetching resources by type:', error);
    throw new Error('Failed to fetch resources: ' + error.message);
  }
};

/**
 * Track resource download/view
 * @param {string} resourceId - Resource ID
 * @param {string} userId - User ID who accessed the resource
 * @returns {Promise<void>}
 */
export const trackResourceAccess = async (resourceId, userId) => {
  try {
    await addDoc(collection(db, 'resourceAccess'), {
      resourceId,
      userId,
      accessedAt: serverTimestamp(),
      type: 'view'
    });
  } catch (error) {
    console.warn('Error tracking resource access:', error);
    // Don't throw error for tracking, as it's not critical
  }
};

/**
 * Get resource access analytics for a project
 * @param {string} projectId - Project ID
 * @returns {Promise<Object>} - Analytics data
 */
export const getResourceAnalytics = async (projectId) => {
  try {
    const resources = await getProjectResources(projectId);
    const resourceIds = resources.map(r => r.id);

    // Initialize analytics object
    const analytics = {
      totalResources: resources.length,
      totalAccess: 0,
      resourcesByType: {},
      mostAccessed: {},
      recentAccess: []
    };

    // If no resources, return empty analytics
    if (resourceIds.length === 0) {
      return analytics;
    }

    // Get access logs - only query if we have resource IDs
    const accessQuery = query(
      collection(db, 'resourceAccess'),
      where('resourceId', 'in', resourceIds),
      orderBy('accessedAt', 'desc')
    );

    const accessSnapshot = await getDocs(accessQuery);
    const accessLogs = accessSnapshot.docs.map(doc => doc.data());

    // Update analytics with actual data
    analytics.totalAccess = accessLogs.length;
    analytics.recentAccess = accessLogs.slice(0, 10);

    // Count by type
    resources.forEach(resource => {
      const type = resource.resourceType || 'document';
      analytics.resourcesByType[type] = (analytics.resourcesByType[type] || 0) + 1;
    });

    // Count access per resource
    accessLogs.forEach(log => {
      analytics.mostAccessed[log.resourceId] = (analytics.mostAccessed[log.resourceId] || 0) + 1;
    });

    return analytics;
  } catch (error) {
    console.error('Error getting resource analytics:', error);
    // Return empty analytics on error instead of throwing
    return {
      totalResources: 0,
      totalAccess: 0,
      resourcesByType: {},
      mostAccessed: {},
      recentAccess: []
    };
  }
};

// File type utilities – returns slug for use with FileTypeIcon component
export const getFileIcon = (resourceType, fileType) => {
  if (resourceType === 'link') return 'link';
  if (!fileType) return 'file';
  const type = fileType.toLowerCase();
  if (type.includes('pdf')) return 'pdf';
  if (type.includes('word') || type.includes('doc')) return 'doc';
  if (type.includes('excel') || type.includes('sheet') || type.includes('csv')) return 'sheet';
  if (type.includes('powerpoint') || type.includes('ppt')) return 'sheet';
  if (type.includes('image') || type.includes('png') || type.includes('jpg') || type.includes('jpeg')) return 'image';
  if (type.includes('video') || type.includes('mp4')) return 'video';
  if (type.includes('audio') || type.includes('mp3')) return 'audio';
  if (type.includes('zip') || type.includes('rar')) return 'zip';
  if (type.includes('html') || type.includes('css') || type.includes('js')) return 'code';
  return 'file';
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const isPreviewable = (fileType) => {
  if (!fileType) return false;
  const type = fileType.toLowerCase();
  return type.includes('pdf') || 
         type.includes('image') || 
         type.includes('video') || 
         type.includes('audio');
}; 